import React, { useEffect, useState } from "react";
import SkillBridgeTable from "./SkillBridgeTable/SkillBridgeTable";
import "./index.scss";
import {
  Button,
  Flex,
  InfoIcon,
  Input,
  SearchIcon,
  Text,
  Tooltip,
  Dialog,
  Dropdown,
} from "@fluentui/react-northstar";
import { Plus } from "react-feather";
import axios from "axios";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

const SkillBridgeSettings = (props) => {
  const [isOpenAddUser, setIsOpenAddUser] = useState(false);
  const [skillBridgeData, setSkillBridgeData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [showError, setShowError] = useState(false);
  const [isValidEmail, setIsValidEmail] = useState(true);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    provider: "",
    role: "Partner",
  });

  const { t } = useTranslation();
  // Using parent translations as placeholder - you can update these later
  const skillBridge = {
    message:
      "Please note that our solution works only with Microsoft and Google accounts.",
    addUserButton: "Add Ministry/Partner",
    listHeaders: {
      name: "Name",
      email: "Email",
      role: "Role",
      is_active: "Status",
      actions: "Actions",
    },
  };

  const providers = ["Microsoft", "Google"];
  const roles = ["Ministry", "Partner"];

  const detectProvider = async (email = "") => {
    if (!email.trim()) return false;
    if (!email.includes("@")) {
      setIsValidEmail(false);
      return false;
    }
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_EP_URL}/api/user/check-email-provider?email=${email}`
      );
      const data = response?.data;
      if (data?.provider === "Microsoft") {
        setForm({ ...form, provider: "microsoft" });
        setIsValidEmail(true);
        return true;
      } else if (data?.provider === "Google") {
        setForm({ ...form, provider: "google" });
        setIsValidEmail(true);
        return true;
      } else {
        setIsValidEmail(false);
        return false;
      }
    } catch (error) {
      console.log("error", error);
      setIsValidEmail(false);
      return false;
    }
  };

  const getAllSkillBridgeData = (search = "") => {
    setIsLoading(true);
    // Placeholder API call - replace with actual endpoint
    axios
      .get(
        `${process.env.REACT_APP_EP_URL}/api/skillBridge/sb-users/partners-ministry${props.user.slug}`,
        {
          headers: {
            Authorization: "Bearer " + props.user.accessToken,
          },
        }
      )
      .then((res) => {
        const rows = Array.isArray(res?.data?.data) ? res.data.data : [];
        if (rows.length) {
          setSkillBridgeData(rows);
        } else {
          skillBridgeData.length && setSkillBridgeData([]);
        }
        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoading(false);
        console.log(err);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    getAllSkillBridgeData();
  }, []);

  // useEffect to call API when search changes
  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      getAllSkillBridgeData(search);
    }, 500); // Debounce of 500ms to prevent API calls on every keystroke

    return () => clearTimeout(debounceTimeout);
  }, [search]);

  // detect provider on email change
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (form.email) {
        detectProvider(form.email);
      }
    }, 500);
    return () => clearTimeout(timeout);
  }, [form.email]);

  const handleReset = () => {
    setForm({
      firstName: "",
      lastName: "",
      email: "",
      provider: "",
      role: "Partner",
    });
    setShowError(false);
    setIsValidEmail(true);
  };

  const handleAddUser = async () => {
    if (
      !form.firstName.trim() ||
      !form.lastName.trim() ||
      !form.email.trim() ||
      !form.role.trim()
    ) {
      setShowError(true);
      return;
    }
    setIsLoading(true);
    const valid = await detectProvider(form.email);
    if (!valid) {
      setIsLoading(false);
      return;
    }
    try {
      const payload = {
        email: form.email,
        firstName: form.firstName,
        lastName: form.lastName,
        provider: form.provider,
        role: form.role,
      };
      await axios.post(
        `${process.env.REACT_APP_EP_URL}/api/skillbridge/sb-users/role${props.user.slug}`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + props.user.accessToken,
          },
        }
      );
      toast.success(`${form.role} added successfully`);
      setIsOpenAddUser(false);
      handleReset();
      getAllSkillBridgeData(search);
    } catch (error) {
      console.log(error);
      toast.error("Adding user failed");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Dialog
        open={isOpenAddUser}
        header={skillBridge.addUserButton}
        className="smallDlg"
        content={
          <div style={{ width: "100%" }}>
            <Flex column gap="gap.small">
              <div>
                <p className="sub-title">First name</p>
                <Input
                  value={form.firstName}
                  placeholder="Enter First Name"
                  onChange={(e) =>
                    setForm({ ...form, firstName: e.target.value })
                  }
                  status={showError && !form.firstName.trim() ? "error" : ""}
                />
              </div>
              <div>
                <p className="sub-title">Last name</p>
                <Input
                  value={form.lastName}
                  placeholder="Enter Last Name"
                  onChange={(e) =>
                    setForm({ ...form, lastName: e.target.value })
                  }
                  status={showError && !form.lastName.trim() ? "error" : ""}
                />
              </div>
              <div>
                <p className="sub-title">Email</p>
                <Input
                  value={form.email}
                  placeholder="Enter Email"
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  status={showError && !form.email.trim() ? "error" : ""}
                />
                {!isValidEmail && (
                  <p style={{ color: "red", fontSize: 13, marginBottom: 0 }}>
                    Please enter a valid Microsoft or Google email
                  </p>
                )}
              </div>
              <div>
                <p className="sub-title">Role</p>
                <Dropdown
                  fluid
                  items={roles}
                  value={form.role}
                  onChange={(e, { value }) => setForm({ ...form, role: value })}
                  className="select-provider-dropdown"
                />
              </div>
              <div>
                <p className="sub-title">Provider</p>
                <Dropdown
                  fluid
                  items={providers}
                  placeholder="Select provider"
                  value={
                    form.provider
                      ? form.provider === "google"
                        ? "Google"
                        : "Microsoft"
                      : ""
                  }
                  onChange={(e, { value }) =>
                    setForm({ ...form, provider: String(value).toLowerCase() })
                  }
                  className="select-provider-dropdown"
                />
                <p style={{ color: "orange", fontSize: 13, paddingTop: 5 }}>
                  Provider is auto-detected from email when possible
                </p>
              </div>
            </Flex>
          </div>
        }
        confirmButton={
          isLoading
            ? { text: "Add", disabled: true, loading: true }
            : { content: "Add", onClick: handleAddUser }
        }
        cancelButton={isLoading ? { text: "Cancel", disabled: true } : "Cancel"}
        onCancel={() => {
          setIsOpenAddUser(false);
          handleReset();
        }}
        headerAction={{
          title: "Close",
          onClick: () => {
            setIsOpenAddUser(false);
            handleReset();
          },
          disabled: isLoading,
        }}
      />
      <Flex className="skillbridge-container" gap="gap.smaller" column>
        <Text
          color="brand"
          content={skillBridge.message}
          weight="semibold"
          style={{ marginLeft: 12 }}
        />
        <div>
          <Flex
            space="between"
            gap="gap.medium"
            style={{ marginBottom: "20px" }}
            wrap
          >
            <Flex gap="gap.smaller">
              <Input
                icon={<SearchIcon />}
                value={search}
                onChange={(_, { value }) => {
                  setSearch(value);
                }}
                style={{ marginLeft: 12 }}
                placeholder="Search by name, email & type"
                onKeyUp={(e) => {
                  if (e.key === "Enter") {
                    getAllSkillBridgeData(search);
                  }
                }}
              />
              <Tooltip
                content="Search by name, email & type"
                trigger={
                  <Button
                    iconOnly
                    icon={<InfoIcon circular outline size="large" />}
                  />
                }
              />
            </Flex>
            <Flex gap="gap.smaller">
              <Button
                icon={<Plus />}
                content={skillBridge.addUserButton}
                onClick={() => {
                  setIsOpenAddUser(true);
                }}
                title={skillBridge.addUserButton}
                style={{
                  maxWidth: "209px",
                }}
                primary
              />
            </Flex>
          </Flex>
          <SkillBridgeTable
            user={props.user}
            data={skillBridgeData}
            isLoading={isLoading}
            getAllSkillBridgeData={getAllSkillBridgeData}
            listHeaders={skillBridge.listHeaders}
          />
        </div>
      </Flex>
    </>
  );
};

export default SkillBridgeSettings;
